import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Chip, Button, IconButton, Typography, Tooltip } from '@mui/material';
import TvIcon from '@mui/icons-material/Tv';
import DashboardIcon from '@mui/icons-material/Dashboard';
import WidgetsIcon from '@mui/icons-material/Widgets';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LinkIcon from '@mui/icons-material/Link';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UserProfile from '../../../business/UserProfile/UserProfile';
import { heritageColors } from '../../../heritageTheme.ts';
import { selectLicense, selectDaysRemainingInTrial, selectActivePlanConfig, selectEffectiveLimit } from '../../../store/actions/licenseActions';
import { isUnlimited } from '../../../utils/licenseUtils';

const TRIAL_TOTAL_DAYS = 14;

const PLAN_META = {
    trial:     { label: 'התנסות חינם', description: '14 יום היכרות – ללא כרטיס אשראי', price: null,      color: '#F59E0B', bg: 'rgba(245,158,11,0.09)', border: 'rgba(245,158,11,0.24)', Icon: AutoAwesomeIcon },
    basic:     { label: 'נֵר',          description: 'לבית כנסת קטן עם מסך אחד',        price: '₪72/חודש', color: '#60A5FA', bg: 'rgba(96,165,250,0.09)',  border: 'rgba(96,165,250,0.22)',  Icon: StarBorderIcon },
    pro:       { label: 'מְנוֹרָה',      description: 'לקהילה עם כמה מסכים',             price: '₪101/חודש',color: '#A78BFA', bg: 'rgba(167,139,250,0.09)', border: 'rgba(167,139,250,0.22)', Icon: WorkspacePremiumIcon },
    community: { label: 'מָאוֹר',        description: 'למוסדות גדולים עם צרכים מורכבים', price: 'בואו נדבר', color: '#34D399', bg: 'rgba(52,211,153,0.09)',  border: 'rgba(52,211,153,0.22)',  Icon: FavoriteIcon },
};

const formatLimit = (val) => {
    if (val === undefined || val === null || isUnlimited(val)) return 'ללא הגבלה';
    return val.toString();
};

const FEATURE_ROWS = [
    { key: 'screens',               label: 'מסכי תצוגה' },
    { key: 'playlistsPerScreen',    label: 'מערכי תצוגה למסך' },
    { key: 'dashboardsPerPlaylist', label: 'לוחות למערך תצוגה' },
    { key: 'savedWidgets',          label: 'רכיבים שמורים' },
    { key: 'versionHistory',        label: 'גרסאות לכל לוח' },
];

const FEATURE_LABELS = {
    advancedDesign: 'עיצוב מתקדם',
    prioritySupport: 'תמיכה מועדפת',
    hardwareBundles: 'חבילות חומרה',
};

const PlanBadgeTooltip = ({ license, daysRemaining, planConfig, dispatch }) => {
    if (!license) return null;
    const meta = PLAN_META[license.plan] || { label: license.plan, description: '', color: '#9CA3AF', Icon: () => null };
    const { Icon } = meta;
    const planLabel = planConfig?.displayName || meta.label;

    const trialEndDate = license?.trialEndsAt
        ? new Intl.DateTimeFormat('he-IL', { day: 'numeric', month: 'long' }).format(
            license.trialEndsAt?.toDate ? license.trialEndsAt.toDate() : new Date(license.trialEndsAt)
          )
        : null;

    const isTrialActive = license.plan === 'trial' && !license.isExpired;
    const progressPct = isTrialActive && daysRemaining !== null
        ? Math.max(0, Math.min(100, (daysRemaining / TRIAL_TOTAL_DAYS) * 100))
        : 0;

    return (
        <Box sx={{ p: 2, minWidth: 215, maxWidth: 245, textAlign: 'right', direction: 'rtl' }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
                <Box sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    bgcolor: meta.bg.replace('0.09', '0.16'),
                    border: `1px solid ${meta.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    mt: 0.2,
                }}>
                    <Icon sx={{ fontSize: 17, color: meta.color }} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: 'rgba(255,255,255,0.92)', lineHeight: 1.2 }}>
                            {planLabel}
                        </Typography>
                        {meta.price && !license.isExpired && (
                            <Typography sx={{ fontSize: '0.7rem', color: meta.color, fontWeight: 600, lineHeight: 1.2 }}>
                                {meta.price}
                            </Typography>
                        )}
                    </Box>
                    <Typography sx={{ fontSize: '0.72rem', color: license.isExpired ? '#F87171' : 'rgba(255,255,255,0.4)', lineHeight: 1.3, mt: 0.35 }}>
                        {license.isExpired ? (license.plan === 'trial' ? 'הניסיון הסתיים' : 'פג תוקף') : meta.description}
                    </Typography>
                </Box>
            </Box>

            {/* Trial: progress bar + countdown */}
            {isTrialActive && daysRemaining !== null && (
                <>
                    <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.07)', mb: 1.5 }} />
                    <Box sx={{ mb: 1.25 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.8 }}>
                            <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>
                                {TRIAL_TOTAL_DAYS} ימים
                            </Typography>
                            <Typography sx={{ fontSize: '0.78rem', color: meta.color, fontWeight: 600 }}>
                                {daysRemaining === 0 ? 'מסתיים היום' : daysRemaining === 1 ? 'נותר יום אחד' : `${daysRemaining} ימים נותרו`}
                            </Typography>
                        </Box>
                        <Box sx={{ height: 5, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                            <Box sx={{
                                height: '100%',
                                width: `${progressPct}%`,
                                background: `linear-gradient(90deg, ${meta.color}88, ${meta.color})`,
                                borderRadius: 3,
                            }} />
                        </Box>
                        {trialEndDate && (
                            <Typography sx={{ fontSize: '0.69rem', color: 'rgba(255,255,255,0.28)', mt: 0.65 }}>
                                עד {trialEndDate}
                            </Typography>
                        )}
                    </Box>
                </>
            )}

            {/* Features / Limits */}
            <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.07)', mb: 1.25 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9, mb: isTrialActive ? 1.5 : 0 }}>
                {FEATURE_ROWS.map(({ key, label, unit }) => {
                    const raw = isTrialActive ? 9999 : (planConfig?.limits?.[key] ?? null);
                    const display = raw !== null ? formatLimit(raw) : '—';
                    const isUnlim = raw === null || isUnlimited(raw);
                    return (
                        <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: '0.74rem', color: isUnlim ? meta.color : 'rgba(255,255,255,0.72)', fontWeight: isUnlim ? 500 : 400 }}>
                                {display}{unit && !isUnlim ? ` ${unit}` : ''}
                            </Typography>
                            <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.38)' }}>
                                {label}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>

            {/* Features flags from DB */}
            {!isTrialActive && planConfig?.features && (
                (() => {
                    const activeFeatures = Object.entries(planConfig.features).filter(([, v]) => v);
                    if (activeFeatures.length === 0) return null;
                    return (
                        <>
                            <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.07)', mb: 1.25 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
                                {activeFeatures.map(([key]) => (
                                    <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'flex-end' }}>
                                        <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.55)' }}>
                                            {FEATURE_LABELS[key] || key}
                                        </Typography>
                                        <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: meta.color, flexShrink: 0 }} />
                                    </Box>
                                ))}
                            </Box>
                        </>
                    );
                })()
            )}

            {/* CTA for trial */}
            {isTrialActive && (
                <>
                    <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.07)', mb: 1.5 }} />
                    <Button
                        size="small"
                        fullWidth
                        onClick={() => dispatch({ type: 'OPEN_UPGRADE_DRAWER', payload: { featureName: 'שדרוג מסלול', requiredPlan: 'basic' } })}
                        sx={{
                            background: 'linear-gradient(90deg, #B45309, #D97706)',
                            color: '#fff',
                            fontWeight: 700,
                            fontSize: '0.76rem',
                            borderRadius: '8px',
                            textTransform: 'none',
                            py: 0.85,
                            boxShadow: '0 2px 10px rgba(217,119,6,0.3)',
                            '&:hover': { background: 'linear-gradient(90deg, #92400E, #B45309)', boxShadow: '0 3px 14px rgba(217,119,6,0.45)' }
                        }}
                    >
                        שדרג עכשיו →
                    </Button>
                </>
            )}
        </Box>
    );
};

const ScreenUrlTooltip = ({ screenPublicUrl, screenName, copySuccess, onCopy }) => (
    <Box sx={{ p: 2, minWidth: 220, maxWidth: 252, textAlign: 'right', direction: 'rtl' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
            <Box sx={{
                width: 34, height: 34, borderRadius: '9px',
                bgcolor: 'rgba(20,184,166,0.15)',
                border: '1px solid rgba(20,184,166,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, mt: 0.2,
            }}>
                <TvIcon sx={{ fontSize: 17, color: '#2DD4BF' }} />
            </Box>
            <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: 'rgba(255,255,255,0.92)', lineHeight: 1.2, mb: 0.35 }}>
                    קישור ציבורי למסך
                </Typography>
                {screenName && (
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1.3 }}>
                        {screenName}
                    </Typography>
                )}
            </Box>
        </Box>

        <Box sx={{ height: '1px', bgcolor: 'rgba(255,255,255,0.07)', mb: 1.25 }} />

        <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.55, mb: 1.25 }}>
            זהו הקישור להציג במסך של בית הכנסת. ניתן לשלוח אותו גם לחברי הקהילה לצפייה מהבית.
        </Typography>

        <Box sx={{
            bgcolor: 'rgba(255,255,255,0.05)', borderRadius: '8px',
            px: 1.25, py: 0.9, mb: 1.25,
            display: 'flex', alignItems: 'center', gap: 0.75, direction: 'ltr',
        }}>
            <Typography sx={{
                fontSize: '0.69rem', color: 'rgba(255,255,255,0.6)',
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
                {screenPublicUrl}
            </Typography>
            <IconButton size="small" onClick={onCopy}
                sx={{ color: copySuccess ? '#34D399' : 'rgba(255,255,255,0.45)', p: 0.4, borderRadius: '6px',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.08)', color: copySuccess ? '#34D399' : 'rgba(255,255,255,0.8)' },
                    transition: 'color 0.15s',
                }}
            >
                {copySuccess
                    ? <CheckIcon sx={{ fontSize: 14 }} />
                    : <ContentCopyIcon sx={{ fontSize: 14 }} />
                }
            </IconButton>
        </Box>

        <Button size="small" fullWidth
            endIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
            onClick={() => window.open(screenPublicUrl, '_blank')}
            sx={{
                color: 'rgba(255,255,255,0.72)', fontSize: '0.76rem', fontWeight: 500,
                textTransform: 'none', borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.12)', py: 0.65,
                justifyContent: 'center',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', borderColor: 'rgba(255,255,255,0.22)' },
            }}
        >
            פתח מסך
        </Button>
    </Box>
);

/**
 * Sidebar - Container של סידבר (תמיד מוצג)
 * מציג ניווט, פרופיל משתמש והגדרות
 * 
 * Container זה רק מעטפת - כל הקומפוננטות הן סטנד-אלון ומטפלות בעצמן ב-Redux
 */
const Sidebar = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { organizationId } = useParams();

    const user = useSelector(state => state.auth.user);
    const organization = useSelector(state => state.organization);
    const license = useSelector(selectLicense);
    const daysRemaining = useSelector(selectDaysRemainingInTrial);
    const planConfig = useSelector(selectActivePlanConfig);
    const planMeta = license ? (PLAN_META[license.plan] || { label: license.plan, color: '#9CA3AF', bg: 'rgba(255,255,255,0.05)', border: 'rgba(255,255,255,0.12)', Icon: () => null }) : null;
    const playlistsPerScreenLimit = useSelector(state => selectEffectiveLimit(state, 'playlistsPerScreen'));
    const dashboardsPerPlaylistLimit = useSelector(state => selectEffectiveLimit(state, 'dashboardsPerPlaylist'));
    const allPlaylists = useSelector(state => state.screens?.playlists || []);

    // 🎨 קריאת כל הדשבורדים מ-Redux לצורך "הרכיבים שלי"
    const allDashboards = useSelector(state => state.screens?.dashboards || []);

    // 🎨 חישוב myWidgets - כל הרכיבים מכל הדשבורדים
    const myWidgets = allDashboards.flatMap(dashboard =>
        (dashboard.current?.widgets || []).map(widget => ({
            ...widget,
            dashboardId: dashboard.dashboardId,
            dashboardName: dashboard.name
        }))
    );

    // ── Screen public URL ──────────────────────────────────────────
    const allScreens = useSelector(state => state.screens?.screens || []);
    const [copySuccess, setCopySuccess] = useState(false);

    const pathParts = location.pathname.split('/');
    const screensIdx = pathParts.indexOf('screens');
    const screenIdFromPath = screensIdx >= 0 && pathParts[screensIdx + 1] ? pathParts[screensIdx + 1] : null;
    const screenIdFromState = location.state?.fromScreenId;
    const currentScreenId = screenIdFromPath || screenIdFromState;
    const currentScreen = currentScreenId ? allScreens.find(s => s.screenId === currentScreenId) : null;
    const orgHash = organization?.orgHash;
    const screenPublicUrl = currentScreen && orgHash
        ? `${window.location.origin}/${orgHash}/${currentScreen.url}`
        : null;

    const currentScreenPlaylists = currentScreen
        ? allPlaylists.filter(p => p.screenId === currentScreen.screenId)
        : [];
    const anyLimitReached = (
        (!isUnlimited(playlistsPerScreenLimit) && currentScreenPlaylists.length >= playlistsPerScreenLimit) ||
        currentScreenPlaylists.some(pl => !isUnlimited(dashboardsPerPlaylistLimit) && (pl.items?.length ?? 0) >= dashboardsPerPlaylistLimit)
    );

    const handleCopyScreenUrl = () => {
        if (!screenPublicUrl) return;
        navigator.clipboard.writeText(screenPublicUrl).then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
        });
    };
    // ──────────────────────────────────────────────────────────────

    // בדיקה איזה דף פעיל כרגע
    const isScreensActive = location.pathname.includes('/screens');
    const isDashboardsActive = location.pathname.includes('/dashboard') && !location.pathname.includes('/screens');
    const isMyWidgetsActive = location.pathname.includes('/my-widgets');

    const handleNavigateToScreens = () => {
        const orgId = organizationId || user?.organizationId;
        if (orgId) {
            navigate(`/${orgId}/screens`);
        }
    };

    const handleNavigateToMyWidgets = () => {
        const orgId = organizationId || user?.organizationId;
        if (orgId) {
            navigate(`/${orgId}/my-widgets`);
        }
    };

    return (
        <Box
            sx={{
                width: '220px',
                height: '100vh',
                background: 'linear-gradient(180deg, #112236 0%, #1a3352 30%, #1e3a5f 100%)',
                color: 'white',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '2px 0 20px rgba(0,0,0,0.22), inset -1px 0 0 rgba(255,255,255,0.04)',
                overflowY: 'auto',
                p: 2
            }}
        >
            {/* Header - לוגו וארגון */}
            <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        background: 'linear-gradient(135deg, #9a7209 0%, #d4a017 100%)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        border: '2px solid rgba(212,160,23,0.4)',
                        boxShadow: '0 0 0 4px rgba(154,114,9,0.12), 0 4px 16px rgba(0,0,0,0.3)'
                    }}
                >
                    {organization?.logoUrl ? (
                        <Box
                            component="img"
                            src={organization.logoUrl}
                            alt={organization?.name || 'Logo'}
                            sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    ) : (
                        <Box
                            component="span"
                            sx={{
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '2.5rem'
                            }}
                        >
                            {organization?.name?.charAt(0) || 'I'}
                        </Box>
                    )}
                </Box>
                <Box sx={{ fontWeight: 700, color: 'rgba(255,255,255,0.95)', fontSize: '1.1rem', letterSpacing: '-0.01em' }}>
                    {organization?.name || 'Inorai'}
                </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid rgba(154,114,9,0.22)', mb: 2 }} />

            {/* Navigation Menu */}
            <List sx={{ px: 0 }}>
                <ListItemButton
                    onClick={handleNavigateToScreens}
                    sx={{
                        borderRadius: '8px',
                        mb: 1,
                        flexDirection: 'row-reverse',
                        gap: 2,
                        bgcolor: isScreensActive ? 'rgba(154,114,9,0.18)' : 'transparent',
                        borderRight: isScreensActive ? '4px solid #c8960c' : '4px solid transparent',
                        boxShadow: isScreensActive ? 'inset 0 1px 0 rgba(255,255,255,0.07)' : 'none',
                        transition: 'background-color 0.2s, box-shadow 0.2s',
                        '&:hover': { bgcolor: isScreensActive ? 'rgba(154,114,9,0.26)' : 'rgba(255,255,255,0.08)' }
                    }}
                >
                    <ListItemText
                        primary="ניהול מסכי תצוגה"
                        sx={{
                            textAlign: 'right',
                            '& .MuiListItemText-primary': {
                                color: 'white',
                                fontSize: '0.9rem',
                                fontWeight: isScreensActive ? 'bold' : 'normal'
                            }
                        }}
                    />
                    <ListItemIcon sx={{ minWidth: 'auto', color: 'white' }}>
                        <TvIcon />
                    </ListItemIcon>
                </ListItemButton>

                <ListItemButton
                    disabled
                    sx={{
                        borderRadius: '8px',
                        mb: 1,
                        opacity: 0.4,
                        flexDirection: 'row-reverse',
                        gap: 2,
                        bgcolor: isDashboardsActive ? 'rgba(154,114,9,0.18)' : 'transparent',
                        borderRight: isDashboardsActive ? '4px solid #c8960c' : '4px solid transparent',
                    }}
                >
                    <ListItemText
                        primary="לוחות"
                        sx={{
                            textAlign: 'right',
                            '& .MuiListItemText-primary': {
                                color: 'white',
                                fontSize: '0.9rem',
                                fontWeight: isDashboardsActive ? 'bold' : 'normal'
                            }
                        }}
                    />
                    <ListItemIcon sx={{ minWidth: 'auto', color: 'white' }}>
                        <DashboardIcon />
                    </ListItemIcon>
                </ListItemButton>

                {/* הרכיבים שלי - ניווט לדף */}
                <ListItemButton
                    onClick={handleNavigateToMyWidgets}
                    sx={{
                        borderRadius: '8px',
                        mb: 1,
                        flexDirection: 'row-reverse',
                        gap: 2,
                        bgcolor: isMyWidgetsActive ? 'rgba(154,114,9,0.18)' : 'transparent',
                        borderRight: isMyWidgetsActive ? '4px solid #c8960c' : '4px solid transparent',
                        boxShadow: isMyWidgetsActive ? 'inset 0 1px 0 rgba(255,255,255,0.07)' : 'none',
                        transition: 'background-color 0.2s, box-shadow 0.2s',
                        '&:hover': { bgcolor: isMyWidgetsActive ? 'rgba(154,114,9,0.26)' : 'rgba(255,255,255,0.08)' }
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
                        <ListItemText
                            primary="הרכיבים שלי"
                            sx={{
                                textAlign: 'right',
                                '& .MuiListItemText-primary': {
                                    color: 'white',
                                    fontSize: '0.9rem',
                                    fontWeight: isMyWidgetsActive ? 'bold' : 'normal'
                                }
                            }}
                        />
                        <Chip
                            label={myWidgets.length}
                            size="small"
                            sx={{
                                bgcolor: heritageColors.gold,
                                color: 'white',
                                fontWeight: 'bold',
                                height: '20px',
                                fontSize: '0.75rem'
                            }}
                        />
                    </Box>
                    <ListItemIcon sx={{ minWidth: 'auto', color: 'white' }}>
                        <WidgetsIcon />
                    </ListItemIcon>
                </ListItemButton>
            </List>

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Screen Public URL Badge */}
            {screenPublicUrl && (
                <Tooltip
                    title={
                        <ScreenUrlTooltip
                            screenPublicUrl={screenPublicUrl}
                            screenName={currentScreen?.name}
                            copySuccess={copySuccess}
                            onCopy={handleCopyScreenUrl}
                        />
                    }
                    placement="right"
                    arrow
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#0c2040',
                                border: '1px solid rgba(255,255,255,0.07)',
                                borderRadius: '14px',
                                boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                                p: 0,
                                maxWidth: 268,
                            }
                        },
                        arrow: { sx: { color: '#0c2040' } }
                    }}
                >
                    <Box sx={{
                        mx: 0.5, mb: 1.5, px: 1.75, py: 1.1,
                        borderRadius: '12px',
                        background: 'rgba(20,184,166,0.08)',
                        border: '1px solid rgba(20,184,166,0.22)',
                        display: 'flex', alignItems: 'center', gap: 1.25,
                        cursor: 'default',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            borderColor: 'rgba(20,184,166,0.42)',
                            boxShadow: '0 0 14px rgba(20,184,166,0.14)',
                        },
                    }}>
                        <LinkIcon sx={{ fontSize: 15, color: '#2DD4BF', opacity: 0.88, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.67rem', color: 'rgba(255,255,255,0.32)', lineHeight: 1, mb: 0.35 }}>
                                {currentScreen?.name || 'מסך פעיל'}
                            </Typography>
                            <Typography sx={{
                                fontSize: '0.83rem', color: 'rgba(255,255,255,0.82)',
                                fontWeight: 600, lineHeight: 1,
                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            }}>
                                קישור ציבורי
                            </Typography>
                        </Box>
                    </Box>
                </Tooltip>
            )}

            {/* Plan Badge */}
            {license && planMeta && (
                <Tooltip
                    title={<PlanBadgeTooltip license={license} daysRemaining={daysRemaining} planConfig={planConfig} dispatch={dispatch} />}
                    placement="right"
                    arrow
                    componentsProps={{
                        tooltip: {
                            sx: {
                                bgcolor: '#0c2040',
                                border: '1px solid rgba(255,255,255,0.07)',
                                borderRadius: '14px',
                                boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                                p: 0,
                                maxWidth: 260,
                            }
                        },
                        arrow: { sx: { color: '#0c2040' } }
                    }}
                >
                    <Box sx={{
                        mx: 0.5,
                        mb: 1.5,
                        px: 1.75,
                        py: 1.1,
                        borderRadius: '12px',
                        background: planMeta.bg,
                        border: `1px solid ${planMeta.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        cursor: 'default',
                        transition: 'border-color 0.2s, box-shadow 0.2s',
                        '&:hover': {
                            borderColor: planMeta.color + '60',
                            boxShadow: `0 0 12px ${planMeta.color}18`,
                        },
                    }}>
                        <planMeta.Icon sx={{ fontSize: 15, color: planMeta.color, opacity: 0.85, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '0.67rem', color: 'rgba(255,255,255,0.32)', lineHeight: 1, mb: 0.35 }}>
                                מסלול
                            </Typography>
                            <Typography sx={{ fontSize: '0.83rem', color: 'rgba(255,255,255,0.82)', fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {planConfig?.displayName || planMeta.label}
                            </Typography>
                        </Box>
                        {license.plan === 'trial' && !license.isExpired && daysRemaining !== null && (
                            <Box sx={{ textAlign: 'center', flexShrink: 0 }}>
                                <Typography sx={{ fontSize: '0.9rem', color: planMeta.color, fontWeight: 700, lineHeight: 1 }}>
                                    {daysRemaining}
                                </Typography>
                                <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.32)', lineHeight: 1.4 }}>
                                    ימים
                                </Typography>
                            </Box>
                        )}
                        {anyLimitReached && !license.isExpired && license.plan !== 'trial' && (
                            <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                bgcolor: '#F59E0B', flexShrink: 0,
                                boxShadow: '0 0 6px rgba(245,158,11,0.6)',
                            }} />
                        )}
                        {license.isExpired && (
                            <Box sx={{
                                px: 0.75,
                                py: 0.3,
                                borderRadius: '6px',
                                bgcolor: 'rgba(248,113,113,0.12)',
                                border: '1px solid rgba(248,113,113,0.28)',
                                flexShrink: 0,
                            }}>
                                <Typography sx={{ fontSize: '0.62rem', color: '#F87171', fontWeight: 600, lineHeight: 1 }}>
                                    פג תוקף
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Tooltip>
            )}

            <Box sx={{ borderTop: '1px solid rgba(154,114,9,0.18)', mb: 2 }} />

            {/* User Profile */}
            <UserProfile dispatch={dispatch} user={user} organization={organization} />
        </Box>
    );
};

export default Sidebar;
